import { ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2, ShieldCheck } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';
import { useTagRequestQuery } from '@/client/api/tag-requests/tag-requests.query';
import {
  useClaimTagRequestMutation,
  useDecideTagRequestMutation,
} from '@/client/api/tag-requests/tag-requests.mutation';
import type { TagDecision, TagRequest } from '@/client/types/tag-requests';
import TagDecisionRow, { EMPTY_DRAFT, type TagDecisionDraft } from './TagDecisionRow';
import TagRequestEvidence from './TagRequestEvidence';
import {
  REQUEST_STATUS_BADGE,
  REQUEST_STATUS_LABEL,
  isDecidable,
  listingLabel,
  personLabel,
  requesterLabel,
  titleCaseTag,
} from './tagRequestStatus';

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="mb-0.5 text-xs text-gray-500">{label}</p>
    <div className="break-words text-sm font-medium text-gray-900">{value ?? '—'}</div>
  </div>
);

const formatDate = (iso?: string | null) => (iso ? moment(iso).format('YYYY-MM-DD HH:mm') : '—');

interface TagRequestReviewDrawerProps {
  /** Row the drawer was opened from — shown until the full detail loads. */
  summary: TagRequest;
  onClose: () => void;
}

const TagRequestReviewDrawer = ({ summary, onClose }: TagRequestReviewDrawerProps) => {
  const { data: detail, isLoading } = useTagRequestQuery(summary.etrid);
  const claim = useClaimTagRequestMutation();
  const decide = useDecideTagRequestMutation();
  // Deciding needs both codes: approving *is* attaching a protected tag (ff-api `_may_decide`).
  const decideGate = useGate(PERMISSIONS.tagRequest.decide);
  const protectedGate = useGate(PERMISSIONS.tag.protectedManage);
  const canDecide = decideGate.allowed && protectedGate.allowed;
  const denyReason = !decideGate.allowed ? decideGate.reason : protectedGate.reason;

  const [drafts, setDrafts] = useState<Record<number, TagDecisionDraft>>({});
  const [reviewerNotes, setReviewerNotes] = useState('');

  const req = detail ?? summary;
  const items = useMemo(() => req.items ?? [], [req.items]);
  const assets = req.assets ?? [];
  const pendingItems = items.filter((i) => i.status === 'pending');
  const editable = canDecide && isDecidable(req.status);
  const tagByItem = new Map(items.map((i) => [i.etriid, titleCaseTag(i.tag_name)]));

  const draftFor = (tagid: number) => drafts[tagid] ?? EMPTY_DRAFT;
  const chosen = pendingItems.filter((i) => draftFor(i.tagid).verdict !== 'pending');
  const approving = chosen.filter((i) => draftFor(i.tagid).verdict === 'approved').length;
  const rejecting = chosen.length - approving;
  const missingReason = chosen.some((i) => draftFor(i.tagid).verdict === 'rejected' && !draftFor(i.tagid).reason);

  const setAll = (verdict: TagDecisionDraft['verdict']) =>
    setDrafts((prev) => {
      const next = { ...prev };
      pendingItems.forEach((i) => (next[i.tagid] = { ...(prev[i.tagid] ?? EMPTY_DRAFT), verdict }));
      return next;
    });

  const takeForReview = async () => {
    try {
      await claim.mutateAsync(req.etrid);
      toast.success('Request is now under review', { richColors: true });
    } catch (error: any) {
      toast.error('Could not take this request', { description: error?.message, richColors: true });
    }
  };

  const save = async () => {
    if (!chosen.length || missingReason) return;
    const decisions: TagDecision[] = chosen.map((i) => {
      const d = draftFor(i.tagid);
      return {
        tagid: i.tagid,
        decision: d.verdict as TagDecision['decision'],
        reason: d.verdict === 'rejected' ? d.reason : undefined,
        notes: d.notes.trim() || undefined,
      };
    });
    try {
      const result = await decide.mutateAsync({ etrid: req.etrid, decisions, reviewerNotes });
      setDrafts({});
      setReviewerNotes('');
      if (result?.apply_error) {
        toast.warning('Decisions saved, but an approved tag did not attach to the listing', {
          description: result.apply_error.error_description || result.apply_error.error,
          richColors: true,
        });
      } else {
        toast.success('Decisions saved', {
          description: `${approving} approved, ${rejecting} rejected${
            pendingItems.length - chosen.length ? `, ${pendingItems.length - chosen.length} still pending` : ''
          }. The lister has been notified.`,
          richColors: true,
        });
      }
    } catch (error: any) {
      toast.error('Could not save decisions', { description: error?.message, richColors: true });
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && !decide.isPending && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-b border-gray-100 p-4 pr-12 text-left">
          <SheetTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-700" />
            Tag request #{req.etrid}
          </SheetTitle>
          <SheetDescription>
            {items.map((i) => titleCaseTag(i.tag_name)).join(', ') || 'Protected tags'} for {listingLabel(req)}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-4">
            <InfoRow
              label="Status"
              value={
                <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${REQUEST_STATUS_BADGE[req.status]}`}>
                  {REQUEST_STATUS_LABEL[req.status] ?? req.status}
                </span>
              }
            />
            <InfoRow label="Submitted" value={formatDate(req.date_submitted ?? req.date_added)} />
            <InfoRow
              label="Listing"
              value={
                <Link to={`/admin/listings/${req.eid}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                  {listingLabel(req)} <ExternalLink className="h-3 w-3 shrink-0" />
                </Link>
              }
            />
            <InfoRow
              label="Requested by"
              value={
                req.oid ? (
                  <>
                    <Link to={`/admin/organizations/${req.oid}`} className="text-blue-600 hover:underline">
                      {requesterLabel(req)}
                    </Link>
                    {req.requester && (
                      <span className="block text-xs font-normal text-gray-500">submitted by {personLabel(req.requester)}</span>
                    )}
                  </>
                ) : req.requester || req.uid ? (
                  <Link to={`/admin/users/${req.requester?.uid ?? req.uid}`} className="text-blue-600 hover:underline">
                    {requesterLabel(req)}
                    {req.requester?.email && req.requester?.name ? (
                      <span className="block text-xs font-normal text-gray-500">{req.requester.email}</span>
                    ) : null}
                  </Link>
                ) : (
                  '—'
                )
              }
            />
            {req.reviewer_uid ? (
              <InfoRow
                label="Reviewer"
                value={
                  <Link to={`/admin/users/${req.reviewer_uid}`} className="text-blue-600 hover:underline">
                    {personLabel(req.reviewer ?? { uid: req.reviewer_uid })}
                  </Link>
                }
              />
            ) : null}
            {req.date_reviewed ? <InfoRow label="Last decision" value={formatDate(req.date_reviewed)} /> : null}
          </div>

          <div>
            <p className="mb-1 text-xs font-medium text-gray-500">The lister's case</p>
            <p className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-800">{req.justification || '—'}</p>
          </div>

          {req.reviewer_notes && (
            <div>
              <p className="mb-1 text-xs font-medium text-gray-500">Reviewer notes (shown to the lister)</p>
              <p className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-800">{req.reviewer_notes}</p>
            </div>
          )}

          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-gray-900">Tags ({items.length})</p>
              {editable && pendingItems.length > 1 && (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setAll('approved')}>Approve all pending</Button>
                  <Button size="sm" variant="outline" onClick={() => setAll('rejected')}>Reject all pending</Button>
                </div>
              )}
            </div>
            {isLoading && !detail ? (
              <Skeleton className="h-24 w-full" />
            ) : (
              <ul className="space-y-2">
                {items.map((item) => (
                  <TagDecisionRow
                    key={item.etriid}
                    item={item}
                    draft={draftFor(item.tagid)}
                    onChange={(d) => setDrafts((prev) => ({ ...prev, [item.tagid]: d }))}
                    editable={editable}
                  />
                ))}
              </ul>
            )}
            {!canDecide && pendingItems.length > 0 && !decideGate.isLoading && (
              <p className="mt-2 text-xs text-gray-500">{denyReason} to decide tags.</p>
            )}
          </div>

          <div>
            <p className="mb-2 text-sm font-semibold text-gray-900">Supporting documents ({assets.length})</p>
            {isLoading && !detail ? (
              <Skeleton className="h-40 w-full" />
            ) : assets.length === 0 ? (
              <p className="text-sm text-gray-500">No documents on this request.</p>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {assets.map((a) => (
                  <TagRequestEvidence
                    key={a.etraid}
                    asset={a}
                    tagLabel={a.etriid != null ? tagByItem.get(a.etriid) : null}
                  />
                ))}
              </div>
            )}
          </div>

          {editable && chosen.length > 0 && (
            <div>
              <label htmlFor="tag-request-reviewer-notes" className="mb-1 block text-xs font-medium text-gray-500">
                Overall note to the lister (optional)
              </label>
              <textarea
                id="tag-request-reviewer-notes"
                value={reviewerNotes}
                onChange={(e) => setReviewerNotes(e.target.value)}
                rows={2}
                className="block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              />
            </div>
          )}
        </div>

        {(req.status === 'submitted' || editable) && (
          <SheetFooter className="flex-row items-center justify-end gap-2 border-t border-gray-100 p-4 sm:space-x-0">
            {editable && chosen.length > 0 && (
              <span className="mr-auto text-xs text-gray-500">
                {approving} to approve, {rejecting} to reject
                {missingReason ? ' — pick a reason for each rejection' : ''}
              </span>
            )}
            {req.status === 'submitted' && (
              <PermissionButton
                code={PERMISSIONS.tagRequest.view}
                variant="outline"
                disabled={claim.isPending}
                onClick={() => void takeForReview()}
              >
                Take for review
              </PermissionButton>
            )}
            {editable && (
              <Button disabled={!chosen.length || missingReason || decide.isPending} onClick={() => void save()}>
                {decide.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                Save decisions
              </Button>
            )}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default TagRequestReviewDrawer;
